"use client";

import { useMemo } from "react";
import { useUser } from "@/components/auth-provider";
import { useMyGroups } from "@/query/hooks/useGroups";
import { useFeatureFlag } from "@/query/hooks/useFeatureFlags";
import type { PreOrderMutateViewer } from "@/app/recap/services/canMutatePreOrder";

export interface ProposalViewer {
  /** Resolved viewer. Pass to `canMutatePreOrder(preOrder, viewer)`. */
  viewer: PreOrderMutateViewer;
  /**
   * `true` only while `useMyGroups` is still on its first load (query status
   * `pending`). Callers stay permissive in this window — the V2 endpoints
   * enforce the same gate server-side. An error settles the query (no longer
   * pending) so the gate applies with an empty group set rather than staying
   * permissive forever.
   */
  isLoadingGroups: boolean;
}

/**
 * Resolves the AI-proposal mutate viewer for the current user. Composes
 * `useUser` + `useMyGroups` + `recap_show_all_confirmed` so recap, the
 * desktop AI queue, and the mobile proposals screen all read the same
 * predicate (mirrors `buildPreOrderVisibility` on the V2 API).
 */
export function useProposalViewer(): ProposalViewer {
  const user = useUser();
  const { data: myGroups, isPending: isLoadingGroups } = useMyGroups();
  const allConfirmedVisible =
    useFeatureFlag("recap_show_all_confirmed") === true;

  const viewer = useMemo<PreOrderMutateViewer>(
    () => ({
      userId: user.id,
      companyPermissions: user.company_permissions,
      groupIds: myGroups?.map((g) => g.id) ?? [],
      allConfirmedVisible,
    }),
    [user.id, user.company_permissions, myGroups, allConfirmedVisible],
  );

  return { viewer, isLoadingGroups };
}
